
import React from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useFormData } from "@/stores/formStore";
import { getFlowSteps, type FlowStepKey } from "@/config/flow-steps";

interface StepperProps {
  currentStep: number;
  totalSteps: number;
  onStepChange: (step: number) => void;
}

const Stepper: React.FC<StepperProps> = ({ currentStep, totalSteps, onStepChange }) => {
  const { formData } = useFormData();
  const { t } = useTranslation("flow");
  const flowSteps = getFlowSteps(!!formData.pulpMode);
  const stepIcons = flowSteps.map((s) => ({ icon: s.icon, label: t(`stepper.${s.key}`), key: s.key }));

  // Check if user has completed the full flow (reached dossier with all required data)
  const hasCompletedFullFlow = () => {
    return formData.details.name && 
           formData.details.age && 
           formData.details.occupation &&
           formData.characteristics.strength > 0 &&
           Object.keys(formData.occupationSkills).length > 0 &&
           Object.keys(formData.personalSkills).length > 0;
  };

  const keyAt = (stepNumber: number): FlowStepKey | undefined => stepIcons[stepNumber - 1]?.key;

  const isStepAccessible = (stepNumber: number) => {
    // Always allow navigation to the current step or backward
    if (stepNumber <= currentStep) {
      return true;
    }

    // If user has completed the full flow, allow free navigation to all steps
    if (hasCompletedFullFlow()) {
      return true;
    }

    const detailsDone = !!(formData.details.name && formData.details.age && formData.details.occupation);
    switch (keyAt(stepNumber)) {
      case "archetype":
      case "talents":
      case "backstory":
      case "equipment":
      case "characteristics":
        return detailsDone;
      case "occupation":
        return detailsDone && formData.characteristics.strength > 0;
      case "skills":
        return detailsDone && Object.keys(formData.occupationSkills).length > 0;
      case "dossier":
        return detailsDone &&
          formData.characteristics.strength > 0 &&
          Object.keys(formData.occupationSkills).length > 0 &&
          Object.keys(formData.personalSkills).length > 0;
      default:
        return false;
    }
  };

  const isStepCompleted = (stepNumber: number) => {
    switch (keyAt(stepNumber)) {
      case "investigator":
        return !!(formData.details.name && formData.details.age && formData.details.occupation);
      case "archetype":
        return currentStep > stepNumber || !!formData.pulp?.archetype;
      case "talents":
        return currentStep > stepNumber || (formData.pulp?.talents?.length ?? 0) > 0;
      case "backstory":
        return currentStep > stepNumber || !!(
          formData.backstory?.ideology ||
          formData.backstory?.significantPeople ||
          formData.backstory?.meaningfulLocations ||
          formData.backstory?.meaningfulObjects ||
          formData.backstory?.traits
        );
      case "equipment":
        return currentStep > stepNumber || !!(
          formData.equipment?.items ||
          (formData.equipment?.weapons && formData.equipment.weapons.length > 0)
        );
      case "characteristics":
        return formData.characteristics.strength > 0;
      case "occupation":
        return Object.keys(formData.occupationSkills).length > 0;
      case "skills":
        return Object.keys(formData.personalSkills).length > 0;
      default:
        return false;
    }
  };

  const currentLabel = stepIcons[currentStep - 1]?.label;

  return (
    <div className="flex flex-col items-center mt-8 px-4">
      {/* Mobile: numbered circles with connecting lines */}
      <div className="flex sm:hidden items-center w-full max-w-sm gap-1">
        {stepIcons.slice(0, totalSteps).map((step, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = !isActive && isStepCompleted(stepNumber);
          const isAccessible = isStepAccessible(stepNumber);

          return (
            <div key={stepNumber} className="contents">
              <button
                onClick={() => isAccessible && onStepChange(stepNumber)}
                disabled={!isAccessible}
                aria-label={t(isCompleted ? "stepper.stepAriaCompleted" : "stepper.stepAria", { step: stepNumber, label: step.label })}
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-all duration-200 border-2",
                  isActive && "bg-brand-cream border-brand-cream text-primary-foreground scale-110",
                  isCompleted && "border-brand-green bg-brand-green/10 text-brand-green",
                  !isActive && !isCompleted && isAccessible && "border-muted-foreground/70 text-muted-foreground",
                  !isActive && !isCompleted && !isAccessible && "border-border text-muted-foreground/50 opacity-50",
                  isAccessible ? "cursor-pointer" : "cursor-not-allowed"
                )}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : stepNumber}
              </button>

              {index < totalSteps - 1 && (
                <div className={cn(
                  "h-0.5 flex-1 min-w-2 mx-0.5 transition-colors duration-200 rounded-full",
                  stepNumber < currentStep ? "bg-brand-green/50" : "bg-muted-foreground/40"
                )} />
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile: current step label */}
      <span className="sm:hidden text-xs mt-2.5 text-brand-cream font-medium">
        {currentLabel}
      </span>

      {/* Desktop: full icon stepper */}
      <div className="hidden sm:flex items-center justify-center space-x-4">
        {stepIcons.slice(0, totalSteps).map((step, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = !isActive && isStepCompleted(stepNumber);
          const isAccessible = isStepAccessible(stepNumber);
          
          const IconComponent = isCompleted ? Check : step.icon;
          
          return (
            <div key={stepNumber} className="contents">
              <div className="flex flex-col items-center">
                <button
                  onClick={() => isAccessible && onStepChange(stepNumber)}
                  disabled={!isAccessible}
                  aria-label={t(isCompleted ? "stepper.stepAriaCompleted" : "stepper.stepAria", { step: stepNumber, label: step.label })}
                  className={cn(
                    "w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-200",
                    "border-2",
                    isActive && "bg-brand-cream border-brand-cream text-primary-foreground scale-110",
                    isCompleted && "border-brand-green text-brand-green hover:border-brand-green/70 hover:text-brand-green/70",
                    !isActive && !isCompleted && isAccessible && "border-muted-foreground text-muted-foreground hover:border-foreground/80 hover:text-foreground/80",
                    !isActive && !isCompleted && !isAccessible && "border-muted-foreground/50 text-muted-foreground/50",
                    isAccessible && "cursor-pointer",
                    !isAccessible && "cursor-not-allowed opacity-50"
                  )}
                >
                  <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                <span className={cn(
                  "text-xs mt-2 text-center",
                  isActive && "text-brand-cream font-medium",
                  isCompleted && "text-brand-green",
                  !isActive && !isCompleted && isAccessible && "text-muted-foreground",
                  !isActive && !isCompleted && !isAccessible && "text-muted-foreground/50"
                )}>
                  {step.label}
                </span>
              </div>
              
              {index < totalSteps - 1 && (
                <div className={cn(
                  "h-0.5 w-6 sm:w-8 transition-colors duration-200",
                  stepNumber < currentStep ? "bg-muted-foreground" : "bg-muted-foreground/50"
                )} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Stepper;
